import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  FlatList,
  Image,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native'
import styled from 'styled-components'
import { colors } from 'infra/color'
import { Body1, Body2 } from 'ui/common/text'
import { ColumnLayout, RowLayout } from 'ui/common/layout'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Ionicons from 'react-native-vector-icons/Ionicons'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { MyPageStackParamList } from 'navigation/mypage-stack'
import { StackNavigationProp } from '@react-navigation/stack'
import { UserSummary } from 'infra/type'
import { useMe, useUserFollowers, useUserFollowings } from 'api/hooks'
import { PROFILE_IMG_PLACEHOLDER_IMAGE } from 'image/icon'
import { getSecureImageUrl } from 'infra/util'
import { TopNavigationBar } from 'ui/common/top-navigation-bar'

type FollowListScreenRouteProp = RouteProp<
  MyPageStackParamList,
  'FollowListScreen'
>
type FollowListScreenNavigationProp = StackNavigationProp<MyPageStackParamList>

const AnimatedBody2 = Animated.createAnimatedComponent(Body2)

export const FollowListScreen = () => {
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<FollowListScreenNavigationProp>()
  const route = useRoute<FollowListScreenRouteProp>()

  const [activeTab, setActiveTab] = useState<'follower' | 'following'>(
    route.params?.initialTab || 'follower',
  )

  const userId = route.params?.userId
  const { data: me } = useMe()
  const targetUserId = userId || me?.id

  const [followers, setFollowers] = useState<UserSummary[]>([])
  const [following, setFollowing] = useState<UserSummary[]>([])
  const [followerOffset, setFollowerOffset] = useState(0)
  const [followingOffset, setFollowingOffset] = useState(0)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const screenWidth = Dimensions.get('window').width
  const scrollViewRef = useRef<ScrollView>(null)
  const scrollX = useRef(
    new Animated.Value(
      route.params?.initialTab === 'following' ? screenWidth : 0,
    ),
  ).current

  const followerColor = scrollX.interpolate({
    inputRange: [0, screenWidth],
    outputRange: [colors.black, colors.gray.v500],
    extrapolate: 'clamp',
  })

  const followingColor = scrollX.interpolate({
    inputRange: [0, screenWidth],
    outputRange: [colors.gray.v500, colors.black],
    extrapolate: 'clamp',
  })

  const LIMIT = 20

  const { data: followersData, isLoading: isLoadingFollowers } =
    useUserFollowers(targetUserId, LIMIT, followerOffset)
  const { data: followingsData, isLoading: isLoadingFollowings } =
    useUserFollowings(targetUserId, LIMIT, followingOffset)

  // Reset lists when target user changes
  useEffect(() => {
    setFollowers([])
    setFollowing([])
    setFollowerOffset(0)
    setFollowingOffset(0)
  }, [targetUserId])

  // Accumulate followers
  useEffect(() => {
    if (followersData?.items) {
      if (followerOffset === 0) {
        setFollowers(followersData.items)
      } else {
        setFollowers((prev) => [...prev, ...followersData.items])
      }
      setIsLoadingMore(false)
    }
  }, [followersData, followerOffset])

  // Accumulate followings
  useEffect(() => {
    if (followingsData?.items) {
      if (followingOffset === 0) {
        setFollowing(followingsData.items)
      } else {
        setFollowing((prev) => [...prev, ...followingsData.items])
      }
      setIsLoadingMore(false)
    }
  }, [followingsData, followingOffset])

  const loadMore = useCallback(
    (type: 'follower' | 'following') => {
      if (isLoadingMore || isLoadingFollowers || isLoadingFollowings) return

      if (type === 'follower') {
        if (followers.length < (followersData?.total || 0)) {
          setIsLoadingMore(true)
          setFollowerOffset((prev) => prev + LIMIT)
        }
      } else {
        if (following.length < (followingsData?.total || 0)) {
          setIsLoadingMore(true)
          setFollowingOffset((prev) => prev + LIMIT)
        }
      }
    },
    [
      followers.length,
      following.length,
      followersData?.total,
      followingsData?.total,
      isLoadingMore,
      isLoadingFollowers,
      isLoadingFollowings,
    ],
  )

  const renderItem = ({ item }: { item: UserSummary }) => (
    <UserListItem
      user={item}
      onPress={() => navigation.push('UserDetailScreen', { userId: item.id })}
    />
  )

  const renderEmptyComponent = (type: 'follower' | 'following') => {
    const isFollower = type === 'follower'
    const isLoading = isFollower ? isLoadingFollowers : isLoadingFollowings
    const currentData = isFollower ? followers : following

    if (isLoading && currentData.length === 0) {
      return (
        <EmptyContainer>
          <ActivityIndicator size='large' color={colors.primary} />
        </EmptyContainer>
      )
    }

    const message = isFollower
      ? '아직 팔로워가 없어요'
      : '아직 팔로잉하는 사람이 없어요'
    const icon = isFollower ? 'people-outline' : 'person-add-outline'

    return (
      <EmptyContainer>
        <Ionicons name={icon} size={48} color={colors.gray.v300} />
        <Body1 style={{ color: colors.gray.v500, marginTop: 16 }}>
          {message}
        </Body1>
      </EmptyContainer>
    )
  }

  const onScrollEnd = (e: any) => {
    const offsetX = e.nativeEvent.contentOffset.x
    const index = Math.round(offsetX / screenWidth)
    setActiveTab(index === 0 ? 'follower' : 'following')
  }

  const handleTabPress = (tab: 'follower' | 'following') => {
    setActiveTab(tab)
    scrollViewRef.current?.scrollTo({
      x: tab === 'follower' ? 0 : screenWidth,
      animated: true,
    })
  }

  const isFollowingTab = route.params?.initialTab === 'following'

  return (
    <Container style={{ paddingTop: insets.top }}>
      <TopNavigationBar
        title={activeTab === 'follower' ? '팔로워' : '팔로잉'}
      />

      <TabContainer style={{ position: 'relative' }}>
        <TabButton
          active={activeTab === 'follower'}
          onPress={() => handleTabPress('follower')}
        >
          <AnimatedBody2
            style={{
              fontWeight: 'bold',
              color: followerColor,
            }}
          >
            {`팔로워 ${followersData?.total || 0}`}
          </AnimatedBody2>
        </TabButton>
        <TabButton
          active={activeTab === 'following'}
          onPress={() => handleTabPress('following')}
        >
          <AnimatedBody2
            style={{
              fontWeight: 'bold',
              color: followingColor,
            }}
          >
            {`팔로잉 ${followingsData?.total || 0}`}
          </AnimatedBody2>
        </TabButton>
        <Animated.View
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: screenWidth / 2,
            height: 2,
            backgroundColor: colors.black,
            transform: [
              {
                translateX: scrollX.interpolate({
                  inputRange: [0, screenWidth],
                  outputRange: [0, screenWidth / 2],
                  extrapolate: 'clamp',
                }),
              },
            ],
          }}
        />
      </TabContainer>

      <Animated.ScrollView
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onScrollEnd}
        contentOffset={{ x: isFollowingTab ? screenWidth : 0, y: 0 }}
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollX } } }],
          { useNativeDriver: false },
        )}
      >
        <View style={{ width: screenWidth, flex: 1 }}>
          <FlatList
            data={followers}
            renderItem={renderItem}
            ListEmptyComponent={() => renderEmptyComponent('follower')}
            ListFooterComponent={
              isLoadingMore && activeTab === 'follower' ? (
                <ActivityIndicator
                  size='small'
                  color={colors.gray.v500}
                  style={{ paddingVertical: 16 }}
                />
              ) : null
            }
            keyExtractor={(item, index) => `${item.id}-${index}`}
            contentContainerStyle={{ padding: 20, flexGrow: 1 }}
            onEndReached={() => loadMore('follower')}
            onEndReachedThreshold={0.5}
          />
        </View>
        <View style={{ width: screenWidth, flex: 1 }}>
          <FlatList
            data={following}
            renderItem={renderItem}
            ListEmptyComponent={() => renderEmptyComponent('following')}
            ListFooterComponent={
              isLoadingMore && activeTab === 'following' ? (
                <ActivityIndicator
                  size='small'
                  color={colors.gray.v500}
                  style={{ paddingVertical: 16 }}
                />
              ) : null
            }
            keyExtractor={(item, index) => `${item.id}-${index}`}
            contentContainerStyle={{ padding: 20, flexGrow: 1 }}
            onEndReached={() => loadMore('following')}
            onEndReachedThreshold={0.5}
          />
        </View>
      </Animated.ScrollView>
    </Container>
  )
}

const UserListItem = ({
  user,
  onPress,
}: {
  user: UserSummary
  onPress: () => void
}) => {
  return (
    <ListItemContainer onPress={onPress}>
      <ProfileImage
        source={
          user?.thumbnailImageUrl
            ? { uri: getSecureImageUrl(user.thumbnailImageUrl) }
            : PROFILE_IMG_PLACEHOLDER_IMAGE
        }
      />
      <Body1 style={{ marginLeft: 12, fontWeight: '500' }}>
        {user.nickname}
      </Body1>
    </ListItemContainer>
  )
}

const EmptyContainer = styled(ColumnLayout)`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding-bottom: 100px;
`

const Container = styled(ColumnLayout)`
  flex: 1;
  background-color: ${colors.white};
`

const TabContainer = styled(RowLayout)`
  border-bottom-width: 1px;
  border-bottom-color: ${colors.gray.v100};
`

const TabButton = styled(TouchableOpacity)<{ active: boolean }>`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
  padding-bottom: 16px;
`

const ListItemContainer = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`

const ProfileImage = styled(Image)`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background-color: ${colors.gray.v200};
`
